import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/auth.service';
import { User, UserProfile, ModuleType, ActionType } from '../types/user.types';
import toast from 'react-hot-toast';

interface AuthContextType {
  user: User | null;
  selectedProfile: UserProfile | null;
  availableProfiles: UserProfile[];
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  selectProfile: (profileId: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (module: ModuleType, action: ActionType) => boolean;
  hasDroit: (droit: string) => boolean;
  hasRight: (right: string) => boolean;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  isOPJ: () => boolean;
  getProfileName: () => string | null;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<UserProfile | null>(null);
  const [availableProfiles, setAvailableProfiles] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Initialiser l'état depuis localStorage au chargement
  useEffect(() => {
    const initAuth = () => {
      const storedUser = authService.getCurrentUser();
      if (storedUser) {
        setUser(storedUser);
      }

      const storedProfiles = localStorage.getItem('available_profiles');
      if (storedProfiles) {
        try {
          setAvailableProfiles(JSON.parse(storedProfiles));
        } catch (error) {
          console.error('Erreur lors du parsing des profils:', error);
        }
      }

      const storedProfileId = localStorage.getItem('selected_profile');
      if (storedProfileId && storedProfiles) {
        try {
          const profiles = JSON.parse(storedProfiles);
          const profile = profiles.find((p: UserProfile) => p.id === storedProfileId);
          if (profile) {
            setSelectedProfile(profile);
          }
        } catch (error) {
          console.error('Erreur lors du parsing du profil sélectionné:', error);
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  /**
   * Connexion
   */
  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      setIsLoading(true);
      const response = await authService.login(email, password, rememberMe);

      console.log('🔐 Réponse de connexion:', {
        user: response.user,
        profilesCount: response.availableProfiles?.length || 0,
        profiles: response.availableProfiles?.map((p: any) => p.nomProfil)
      });

      setUser(response.user);

      if (response.availableProfiles && response.availableProfiles.length > 0) {
        setAvailableProfiles(response.availableProfiles);
        localStorage.setItem('available_profiles', JSON.stringify(response.availableProfiles));

        // Sélectionner automatiquement le premier profil
        const firstProfile = response.availableProfiles[0];
        setSelectedProfile(firstProfile);
        localStorage.setItem('selected_profile', firstProfile.id);
        console.log('🔐 Profil sélectionné automatiquement:', firstProfile.nomProfil);
      }

      toast.success('Connexion réussie');
    } catch (error: any) {
      console.error('Erreur de connexion:', error);
      toast.error(error.response?.data?.message || 'Erreur de connexion');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Sélection d'un profil
   */
  const selectProfile = async (profileId: string) => {
    try {
      setIsLoading(true);
      const response = await authService.selectProfile(profileId);

      setUser(response.user);

      const profile = availableProfiles.find(p => p.id === profileId);
      if (profile) {
        setSelectedProfile(profile);
      }

      toast.success('Profil sélectionné');
    } catch (error: any) {
      console.error('Erreur de sélection de profil:', error);
      toast.error(error.response?.data?.message || 'Erreur de sélection de profil');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Déconnexion
   */
  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setSelectedProfile(null);
      setAvailableProfiles([]);
      toast.success('Déconnexion réussie');
    } catch (error: any) {
      console.error('Erreur de déconnexion:', error);
      toast.error('Erreur de déconnexion');
    }
  };

  /**
   * Vérifier une permission spécifique (module + action)
   */
  const hasPermission = (module: ModuleType, action: ActionType): boolean => {
    if (!user) return false;

    // Administrateur a toutes les permissions
    if (user.role === 'ADMINISTRATEUR') return true;

    // Vérifier dans les permissions de l'utilisateur
    return user.permissions?.some(
      permission =>
        permission.module === module &&
        permission.actions.includes(action)
    ) || false;
  };

  /**
   * Vérifier un droit (legacy system)
   */
  const hasDroit = (droit: string): boolean => {
    if (!user) return false;

    // Administrateur a tous les droits
    if (user.role === 'ADMINISTRATEUR') return true;

    return user.droits?.includes(droit) || false;
  };

  /**
   * Vérifier un droit (nouveau système via profil)
   */
  const hasRight = (right: string): boolean => {
    if (!selectedProfile && !user) return false;

    // Administrateur a tous les droits
    if (user?.role === 'ADMINISTRATEUR') return true;

    // Vérifier dans le profil sélectionné
    if (selectedProfile) {
      return selectedProfile.droits?.includes(right) || false;
    }

    // Fallback sur les droits de l'utilisateur
    return user?.droits?.includes(right) || false;
  };

  /**
   * Vérifier si l'utilisateur est administrateur (local OU global).
   * Inclut ADMIN, ADMINISTRATEUR et SUPER_ADMIN.
   */
  const isAdmin = (): boolean => {
    const role = user?.role?.toString().toUpperCase();
    return (
      role === 'ADMINISTRATEUR' ||
      role === 'ADMIN' ||
      role === 'SUPER_ADMIN'
    );
  };

  /**
   * Vérifier si l'utilisateur est SUPER administrateur (multi-pays).
   * Uniquement ce rôle voit toute la plateforme (toutes juridictions, tous pays).
   */
  const isSuperAdmin = (): boolean => {
    const role = user?.role?.toString().toUpperCase();
    return role === 'SUPER_ADMIN';
  };

  /**
   * Vérifier si l'utilisateur a le profil OPJ
   */
  const isOPJ = (): boolean => {
    // Debug: afficher les données utilisateur pour diagnostic
    console.log('🔍 isOPJ check - user:', {
      role: user?.role,
      fonction: (user as any)?.fonction,
    });
    console.log('🔍 isOPJ check - selectedProfile:', selectedProfile);
    console.log('🔍 isOPJ check - availableProfiles:', availableProfiles);

    // Vérifier d'abord le rôle de l'utilisateur (insensible à la casse)
    const userRole = user?.role?.toString().toUpperCase();
    if (userRole === 'OPJ') {
      console.log('🔒 isOPJ: utilisateur a le rôle OPJ');
      return true;
    }

    // Vérifier la fonction de l'utilisateur (insensible à la casse)
    const userFonction = (user as any)?.fonction?.toString().toUpperCase();
    if (userFonction === 'OPJ') {
      console.log('🔒 isOPJ: utilisateur a la fonction OPJ');
      return true;
    }

    // Vérifier le profil sélectionné (insensible à la casse)
    if (selectedProfile) {
      const profilNom = ((selectedProfile as any).nomProfil || '').toString().toUpperCase();
      const profilNestedNom = ((selectedProfile as any).profil?.nomProfil || '').toString().toUpperCase();
      if (profilNom === 'OPJ' || profilNestedNom === 'OPJ') {
        console.log('🔒 isOPJ: profil sélectionné est OPJ');
        return true;
      }
    }

    // Vérifier tous les profils disponibles
    if (availableProfiles && availableProfiles.length > 0) {
      const hasOPJProfile = availableProfiles.some((profile: any) => {
        const nom = (profile.nomProfil || '').toString().toUpperCase();
        return nom === 'OPJ';
      });
      if (hasOPJProfile) {
        console.log('🔒 isOPJ: un des profils disponibles est OPJ');
        return true;
      }
    }

    console.log('🔒 isOPJ: aucun critère OPJ trouvé, retourne false');
    return false;
  };

  /**
   * Obtenir le nom du profil sélectionné
   */
  const getProfileName = (): string | null => {
    if (!selectedProfile) return null;
    return (selectedProfile as any).nomProfil ||
           (selectedProfile as any).profil?.nomProfil ||
           null;
  };

  /**
   * Rafraîchir les données de l'utilisateur
   */
  const refreshUser = async () => {
    try {
      // Rafraîchir le token pour obtenir les données à jour
      await authService.refreshToken();
      const updatedUser = authService.getCurrentUser();
      if (updatedUser) {
        setUser(updatedUser);
      }
    } catch (error) {
      console.error('Erreur lors du rafraîchissement de l\'utilisateur:', error);
    }
  };

  const value: AuthContextType = {
    user,
    selectedProfile,
    availableProfiles,
    isAuthenticated: !!user,
    isLoading,
    login,
    selectProfile,
    logout,
    hasPermission,
    hasDroit,
    hasRight,
    isAdmin,
    isSuperAdmin,
    isOPJ,
    getProfileName,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook pour utiliser le contexte d'authentification
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
};

export default AuthContext;
